import React, { useEffect, useState } from "react";
import { Calendar, MapPin, Trophy } from "lucide-react";
import { Reveal } from "./Reveal";

const useCountdown = (target) => {
  const [t, setT] = useState({ d: 0, h: 0, m: 0, s: 0, done: !target });
  useEffect(() => {
    if (!target) return;
    const tick = () => {
      const diff = new Date(target).getTime() - Date.now();
      if (diff <= 0) {
        setT({ d: 0, h: 0, m: 0, s: 0, done: true });
        return;
      }
      setT({
        d: Math.floor(diff / 86400000),
        h: Math.floor((diff / 3600000) % 24),
        m: Math.floor((diff / 60000) % 60),
        s: Math.floor((diff / 1000) % 60),
        done: false,
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [target]);
  return t;
};

const Unit = ({ value, label }) => (
  <div className="flex flex-col items-center">
    <span className="heading text-5xl md:text-6xl text-cream tabular-nums">
      {String(value).padStart(2, "0")}
    </span>
    <span className="text-[0.6rem] uppercase tracking-[0.25em] text-silver/50 mt-2">{label}</span>
  </div>
);

export const Schedule = ({ settings }) => {
  const offseason = !settings || settings.schedule_offseason || !settings.next_game_date;
  const cd = useCountdown(offseason ? null : settings?.next_game_date);

  return (
    <section id="schedule" data-testid="schedule-section" className="py-24 md:py-32 px-6 md:px-12 lg:px-16 bg-matte">
      <Reveal>
        <div className="text-center mb-14">
          <p className="eyebrow mb-4">Next Up</p>
          <h2 className="heading text-5xl sm:text-6xl lg:text-7xl text-cream">Where To Next?</h2>
        </div>
      </Reveal>

      <Reveal delay={0.1}>
        <div className="max-w-4xl mx-auto border border-silver/15 bg-charcoal/30 p-10 md:p-16 text-center">
          {offseason ? (
            <div>
              <p className="heading text-4xl md:text-5xl text-stone mb-5">No Games Right Now.</p>
              <p className="text-cream/70 text-lg">Just working. Preparing for the next opportunity.</p>
            </div>
          ) : (
            <div>
              <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3 mb-10 text-cream/80">
                <span className="flex items-center gap-2"><Trophy size={16} className="text-stone" /> {settings.next_game_opponent}</span>
                <span className="flex items-center gap-2"><MapPin size={16} className="text-stone" /> {settings.next_game_location}</span>
                <span className="flex items-center gap-2"><Calendar size={16} className="text-stone" /> {settings.next_game_league}</span>
              </div>
              <div className="grid grid-cols-4 gap-4 max-w-md mx-auto">
                <Unit value={cd.d} label="Days" />
                <Unit value={cd.h} label="Hrs" />
                <Unit value={cd.m} label="Min" />
                <Unit value={cd.s} label="Sec" />
              </div>
            </div>
          )}
        </div>
      </Reveal>
    </section>
  );
};
